import React, { useEffect } from 'react';
import { Layout, Affix, Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ACCOUNT_PAGE, HOME_PAGE, LOGIN_PAGE } from "../../configuration/paths";
import { useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, updateAuth, clearTrafficMetrics } from "../../store";
import { remove } from "../../store/localstorage";
import useIsMobile from "../../hooks/useIsMobile";
import BottomTabBar from "./BottomTabBar";
import { getNavItems } from "./navItems";

const { Header, Content, Footer } = Layout;

const Index = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const loggedIn = !!useSelector(state => state.app.auth.token)
  const role = useSelector(state => state.app.profile.role)
  // "System admin" = same definition the backend/route enforce (is_staff/is_superuser),
  // with Site Admin (role 'special') included. Gates the Device Update Logs link.
  const isSysAdmin = useSelector(state => {
    const p = state.app.profile
    return !!(p && (p.is_staff || p.is_superuser || p.role === 'special'))
  })

  const isMobile = useIsMobile()
  const { tabs, moreRows } = getNavItems(role, isSysAdmin)
  const isActive = (key) => key === ACCOUNT_PAGE ? pathname === key : pathname.startsWith(key)
  // Full-screen app views (dashboard map, alerts) fill the viewport under the header; a footer
  // below them pushes the page past 100vh and makes the sticky banner peek through on scroll.
  const fullScreenPage = pathname.startsWith('/dashboard') || pathname.startsWith('/alarm_history')

  useEffect(() => {
    if (loggedIn) {
      dispatch(getProfile())
    }
  }, [loggedIn, dispatch])

  // Track the real visible height (visual viewport) into --app-height so the mobile
  // shell never lets its bottom tab bar slip under Safari's floating address-bar pill
  // or Chrome's toolbar. CSS svh/dvh don't account for Safari's overlay pill; the
  // visualViewport API reports the area actually visible between the browser chrome.
  useEffect(() => {
    const vv = window.visualViewport
    const setAppHeight = () => {
      const h = vv && vv.height ? vv.height : window.innerHeight
      // Ignore degenerate transient values (iOS reports tiny heights while a screenshot
      // overlay / share sheet is up); those would shrink the shell and leave the bottom
      // tab bar stranded mid-screen until a refresh.
      if (h < 200) return
      document.documentElement.style.setProperty('--app-height', `${Math.round(h)}px`)
    }
    // Re-measure when the page comes back to the foreground (after a screenshot/app-switch/
    // share, iOS doesn't always fire a resize, so the stale height would persist).
    const remeasureSoon = () => { setAppHeight(); setTimeout(setAppHeight, 300) }
    const onVisible = () => { if (document.visibilityState === 'visible') remeasureSoon() }

    setAppHeight()
    if (vv) {
      vv.addEventListener('resize', setAppHeight)
      vv.addEventListener('scroll', setAppHeight)
    }
    window.addEventListener('resize', setAppHeight)
    window.addEventListener('orientationchange', remeasureSoon)
    window.addEventListener('pageshow', remeasureSoon)
    window.addEventListener('focus', remeasureSoon)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (vv) {
        vv.removeEventListener('resize', setAppHeight)
        vv.removeEventListener('scroll', setAppHeight)
      }
      window.removeEventListener('resize', setAppHeight)
      window.removeEventListener('orientationchange', remeasureSoon)
      window.removeEventListener('pageshow', remeasureSoon)
      window.removeEventListener('focus', remeasureSoon)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const doLogout = () => {
    dispatch(updateAuth({ token: null }))
    dispatch(clearTrafficMetrics())
    remove("auth_token")
    navigate(LOGIN_PAGE)
  }

  // "More" dropdown mirrors the mobile More sheet (extra pages + Log out).
  const moreMenu = {
    items: [
      ...moreRows.map(r => ({ key: r.key, label: r.label })),
      { type: 'divider' },
      { key: '__logout__', label: 'Log out', danger: true },
    ],
    onClick: ({ key }) => { if (key === '__logout__') { doLogout(); return } navigate(key) },
  }

  return (
    <Layout className={`layout${isMobile ? ' rz-mobile' : ''}`}>
      {!isMobile && (
        <Affix offsetTop={0}>
          <Header className="rz-deskhead">
            <div className="logo" onClick={() => navigate(HOME_PAGE)} />
            {loggedIn ? (
              <nav className="rz-topnav">
                {tabs.map(t => t.key === '__more__' ? (
                  <Dropdown key="more" trigger={['click']} menu={moreMenu}>
                    <button className="rz-topnav-btn">{t.label} <DownOutlined /></button>
                  </Dropdown>
                ) : (
                  <button key={t.key} className={`rz-topnav-btn${isActive(t.key) ? ' on' : ''}`} onClick={() => navigate(t.key)}>
                    {t.label}
                  </button>
                ))}
              </nav>
            ) : (
              <nav className="rz-topnav">
                <button className="rz-topnav-btn" onClick={() => navigate(LOGIN_PAGE)}>Log in</button>
              </nav>
            )}
          </Header>
        </Affix>
      )}
      {isMobile && (
        <div className="rz-topbar">
          <div className="logo" onClick={() => navigate(HOME_PAGE)} />
          <span className="rz-topbar-title" onClick={() => navigate(HOME_PAGE)}>ROADZ Armor</span>
          {!loggedIn && <button className="rz-topbar-login" onClick={() => navigate(LOGIN_PAGE)}>Log in</button>}
        </div>
      )}
      <Content>{children}</Content>
      {isMobile && loggedIn && (
        <BottomTabBar role={role} isSysAdmin={isSysAdmin} onLogout={doLogout} />
      )}
      {!isMobile && !fullScreenPage && (
        <Footer style={{ textAlign: 'center' }}>{`Lantern LLC © ${new Date().getFullYear()}`}</Footer>
      )}
    </Layout>
  );
}

export default Index;
